/**
 * Undo/Redo history for grid state.
 * Stores snapshots of grid + palette (Command pattern).
 */

const MAX_HISTORY = 50;

export interface GridSnapshot {
  readonly grid: readonly (readonly number[])[];
  readonly palette: readonly { readonly index: number; readonly beadId: string; readonly beadCode: string; readonly beadHex: string; readonly label: string; readonly count: number }[];
}

interface HistoryState {
  past: GridSnapshot[];
  future: GridSnapshot[];
}

const history: HistoryState = {
  past: [],
  future: [],
};

/** Push current state onto the undo stack before a change. */
export function pushSnapshot(snapshot: GridSnapshot): void {
  history.past.push(snapshot);
  if (history.past.length > MAX_HISTORY) {
    history.past.shift();
  }
  // Any new action clears redo stack
  history.future = [];
}

/** Pop from undo stack, push current onto redo stack. Returns previous state or null. */
export function undo(current: GridSnapshot): GridSnapshot | null {
  const prev = history.past.pop();
  if (!prev) return null;
  history.future.push(current);
  return prev;
}

/** Pop from redo stack, push current onto undo stack. Returns next state or null. */
export function redo(current: GridSnapshot): GridSnapshot | null {
  const next = history.future.pop();
  if (!next) return null;
  history.past.push(current);
  return next;
}

export function canUndo(): boolean {
  return history.past.length > 0;
}

export function canRedo(): boolean {
  return history.future.length > 0;
}

export function clearHistory(): void {
  history.past = [];
  history.future = [];
}

export function historySize(): { undo: number; redo: number } {
  return { undo: history.past.length, redo: history.future.length };
}
